import { useEffect, useState } from "react";

import styled from "styled-components/macro";

import API from "Boards/API";
import { useBoards } from "Boards/BoardContext";
import { transformSaveCellShapeToRowShape } from "Boards/Utils/dataTransformations";
import useGroupOrSignupID from "Boards/useGroupOrSignupID";
import ExtendedField from "CrossFeatureComponents/ExtendedFields/ExtendedField";
import { convertResponseDataForBackend } from "CrossFeatureComponents/ExtendedFields/dataTransformationHelpers";
import isEqual from "lodash/isEqual";

import FnrButton, { ButtonText } from "UI/FnrButton3";
import { FnrPopover } from "UI/FnrPopover2";

import getSelectedText from "Utilities/getSelectedText";

import { ROW_TYPES } from "../../const";

export default function ExtendedFieldPopover({
  buttonRef,
  showPopover,
  setShowPopover,
  field = {},
  initialValue,
  cellData = {},
  onSave,
  id,
}) {
  const [fieldValue, setFieldValue] = useState(initialValue);
  const [mostRecentInitVal, setMostRecentInitVal] = useState(initialValue);
  const [error, setError] = useState("");
  const [{ boardID, columns }, dispatch] = useBoards();
  const groupOrSignupID = useGroupOrSignupID();

  const { columnID, entityID } = cellData;

  // Used to update cell value when a Shared Field cell value is updated
  useEffect(
    function overrideInputValWithPropVal() {
      if (
        // Using lodash isEqual here because date & address fields use an object as their value
        isEqual(initialValue, mostRecentInitVal) ||
        isEqual(initialValue, fieldValue)
      ) {
        return;
      }

      setFieldValue(initialValue);
      setMostRecentInitVal(initialValue);
    },
    [initialValue, fieldValue, mostRecentInitVal]
  );

  const getApiCall = () => {
    const postData = {
      boardID,
      columnID,
      entityID,
      entityType: "user",
      filters: window.fnReactBus.getCurrentFilterRequest(),
      value: convertResponseDataForBackend(fieldValue, field),
      ...groupOrSignupID,
    };

    return API.saveData(postData);
  };

  const updateCellValue = (entityID, value) => {
    dispatch({
      type: "Set Cell Value",
      payload: {
        entityID,
        columnID,
        value,
      },
    });
  };

  const handleSuccessfulSave = ({ relatedEntities }) => {
    setShowPopover(false);

    const { typeDictID } = columns.find((col) => col.ID === columnID) || {};
    const transformedValue = transformSaveCellShapeToRowShape(
      typeDictID,
      convertResponseDataForBackend(fieldValue, field)
    );

    updateCellValue(entityID, transformedValue);
    if (Array.isArray(relatedEntities) && relatedEntities.length > 0) {
      relatedEntities.forEach((relatedEntity) => {
        if (
          relatedEntity.entityType === ROW_TYPES.USER &&
          typeof relatedEntity.entityID === "number" &&
          relatedEntity.entityID > 0 &&
          typeof transformedValue !== "undefined"
        )
          updateCellValue(relatedEntity.entityID, transformedValue);
      });
    }
    onSave && onSave(transformedValue);
  };

  const handleUnsuccessfulSave = (err) => {
    console.log({ err });
    setError(
      "Oh no, something went wrong! Please contact help@flocknote.com if this error persists."
    );
  };

  const handleCancelClick = () => {
    setFieldValue(initialValue);
    setShowPopover(false);
  };

  const handleShouldClose = (e) => {
    //const selectedText = getSelectedText();
    // if (!selectedText?.length) setShowPopover(false);
  };

  const handleFieldChange = (newVal) => {
    setFieldValue(newVal);
  };

  return (
    <FnrPopover
      buttonRef={buttonRef}
      show={showPopover}
      onShouldClose={handleShouldClose}
      id={id}
    >
      <Content onClick={(e) => e.stopPropagation()}>
        <ExtendedField
          key={field.ID || field.fieldID}
          field={field}
          onChange={handleFieldChange}
          value={fieldValue}
          error={error}
          accentColor="#278dc1"
          labelColor="#fff"
        />
        <SaveAndCancelButtons
          apiCall={getApiCall()}
          onSuccess={handleSuccessfulSave}
          onFail={handleUnsuccessfulSave}
          onCancel={handleCancelClick}
        />
      </Content>
    </FnrPopover>
  );
}

const Content = styled.div`
  padding: 8px;
  min-width: 375px;
  max-width: 400px;
  max-height: 450px;
  overflow: auto;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

function SaveAndCancelButtons({
  apiCall,
  onSuccess,
  onFail,
  onCancel,
  disabled,
  saveText = "Save",
  cancelText = "Cancel",
}) {
  //   const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!saving) return;

    apiCall
      .post()
      .then((resp) => {
        setSaving(false);
        onSuccess && onSuccess(resp);
      })
      .catch((err) => {
        setSaving(false);
        onFail && onFail(err);
      });

    return () => apiCall.cancel();
  }, [saving, apiCall, onSuccess, onFail]);

  return (
    <ButtonsWrapper>
      <FnrButton
        color="gray"
        size="medium"
        style={{ width: "fit-content" }}
        onClick={() => onCancel && onCancel()}
        disabled={disabled || saving}
      >
        <ButtonText>{cancelText}</ButtonText>
      </FnrButton>
      <FnrButton
        color="blue"
        size="medium"
        style={{ width: "fit-content" }}
        onClick={() => setSaving(true)}
        disabled={disabled || saving}
        loading={saving}
      >
        <ButtonText>{saveText}</ButtonText>
      </FnrButton>
    </ButtonsWrapper>
  );
}

const ButtonsWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
`;
